import { clsx } from '@libs/common-next';
import type { PageProps } from '@libs/core';
import type { Entry, UnresolvedLink } from 'contentful';
import { buildPage, isResolvedLinkEntry } from '../../utils';

const ALLOWED_PLP_CONTENT_TYPES = [
    'cardUp',
    'banner',
    'tile6Up',
    'featureBanner',
    'productCarousel',
    'offerDetailsModal',
    'faq',
    'seoBlock',
];

/**
 * When product grid is shown we are rendering only specified content types.
 * This is to allow us to treat PLPPage model as a "Content Page" when
 * product grid is hidden.
 */
function filterPLPItems(
    items: (Entry | UnresolvedLink<'Entry'>)[] = [],
    isProductGridHidden = false
) {
    if (isProductGridHidden) {
        return items;
    }

    return items.filter(
        entry =>
            isResolvedLinkEntry(entry) &&
            ALLOWED_PLP_CONTENT_TYPES.includes(entry.sys.contentType?.sys?.id)
    );
}

export function ProductListPage({ entry, placement }: Readonly<PageProps>) {
    const { hideProductGrid, items, bottomItems } = entry?.fields ?? {};

    const itemsToBuild = placement === 'bottom' ? bottomItems : items;

    const filteredItemsToBuild = filterPLPItems(
        itemsToBuild as Entry[],
        hideProductGrid as boolean
    );

    const children = buildPage(
        filteredItemsToBuild as Entry[],
        entry?.fields?.pageId as string
    );

    return (
        <div
            className={clsx(
                'product-list-page',
                placement === 'top' && 'product-list-page--top',
                placement === 'bottom' && 'product-list-page--bottom'
            )}
            data-testid="plp-layout-container"
        >
            {children}
        </div>
    );
}
